/**
 * Plan versions (§7.7): a plan version is one applied changeset. planAt folds to a version; planDiff compares two
 * versions structurally; inverseOf builds an undo from a changeset's own events (their `before` values); revertTo
 * turns "go back to v" into forward commands. History is never rewritten, and execution facts never revert.
 */
import { briefHash } from "./brief"
import { fold } from "./evolve"
import { canonicalJson } from "./hash"
import { initialState, isOpenItem } from "./state"
import { isContainer, isPlannable } from "./tree"
import type {
  Agent,
  Area,
  ChangesetId,
  EntityRef,
  Goal,
  GraphCommand,
  Item,
  ItemStatus,
  Logged,
  ProjectEvent,
  ProjectState,
} from "./types"

/** State at plan version v: the log folded up to and including ChangesetApplied(v). */
export const planAt = (
  events: ReadonlyArray<Logged>,
  version: number,
  from: ProjectState = initialState,
): ProjectState => {
  if (version <= from.planVersion) return from
  const cut = events.findIndex(([e]) => e._tag === "ChangesetApplied" && e.planVersion === version)
  return fold(from, cut === -1 ? events : events.slice(0, cut + 1))
}

const same = (a: unknown, b: unknown): boolean => canonicalJson(a) === canonicalJson(b)

/** Pick the fields of `a` that differ from `b`, among `keys`. */
const diffFields = <T extends object>(a: T, b: T, keys: ReadonlyArray<keyof T>): Partial<T> => {
  const out: Partial<T> = {}
  for (const k of keys) if (!same(a[k], b[k])) out[k] = b[k]
  return out
}

const PLAN_FIELDS = {
  goal: [
    "title",
    "why",
    "body",
    "deadline",
    "kpiIds",
    "tags",
    "status",
    "target",
    "criteria",
    "rank",
  ] as ReadonlyArray<keyof Goal>,
  item: [
    "title",
    "why",
    "body",
    "type",
    "kind",
    "tags",
    "paths",
    "integrations",
    "placement",
    "hands",
    "parentId",
    "goalIds",
    "areaId",
    "owner",
    "assignee",
    "status",
    "dueAt",
    "notBefore",
    "recurrence",
    "checks",
    "review",
    "bet",
    "rank",
  ] as ReadonlyArray<keyof Item>,
  area: [
    "name",
    "color",
    "intakeAgentId",
    "placement",
    "powers",
    "wip",
    "interfaces",
    "charter",
    "status",
  ] as ReadonlyArray<keyof Area>,
  agent: ["name", "title", "avatar", "hands", "wake", "areaId", "charter", "mind", "status"] as ReadonlyArray<
    keyof Agent
  >,
}

export interface PlanDiffEntry {
  readonly ref: EntityRef
  readonly op: "added" | "removed" | "changed"
  readonly fields: ReadonlyArray<string>
  readonly changesetIds: ReadonlyArray<ChangesetId>
}

/** Structural diff of two plan states, each record tagged with the changesets (from `events`) that touched it. */
export const planDiff = (
  from: ProjectState,
  to: ProjectState,
  events: ReadonlyArray<Logged> = [],
): { readonly from: number; readonly to: number; readonly changes: ReadonlyArray<PlanDiffEntry> } => {
  const touched = new Map<string, Set<ChangesetId>>()
  for (const [e, m] of events) {
    if (m.changesetId === null || m.seq <= from.seq || m.seq > to.seq) continue
    for (const id of idsOf(e)) {
      const set = touched.get(id) ?? new Set()
      set.add(m.changesetId)
      touched.set(id, set)
    }
  }
  const changes: PlanDiffEntry[] = []
  const cmp = <T extends { readonly id: string }>(
    tag: EntityRef["_tag"],
    a: ReadonlyArray<T>,
    b: ReadonlyArray<T>,
    keys: ReadonlyArray<keyof T>,
  ) => {
    const ref = (id: string) => ({ _tag: tag, id }) as EntityRef
    const cs = (id: string) => [...(touched.get(id) ?? [])]
    for (const x of b) {
      const old = a.find((y) => y.id === x.id)
      if (!old) changes.push({ ref: ref(x.id), op: "added", fields: [], changesetIds: cs(x.id) })
      else {
        const fields = Object.keys(diffFields(old, x, keys))
        if (fields.length > 0) changes.push({ ref: ref(x.id), op: "changed", fields, changesetIds: cs(x.id) })
      }
    }
    for (const x of a)
      if (!b.some((y) => y.id === x.id))
        changes.push({ ref: ref(x.id), op: "removed", fields: [], changesetIds: cs(x.id) })
  }
  cmp("goal", from.goals, to.goals, PLAN_FIELDS.goal)
  cmp("kpi", from.kpis, to.kpis, ["name", "unit", "role", "source", "rollsUpTo", "baseline"])
  cmp("area", from.areas, to.areas, PLAN_FIELDS.area)
  cmp("agent", from.agents, to.agents, PLAN_FIELDS.agent)
  cmp("item", from.items, to.items, PLAN_FIELDS.item)
  cmp("edge", from.edges, to.edges, ["kind", "from", "to", "interfaceKey", "note"])
  return { from: from.planVersion, to: to.planVersion, changes }
}

/** Record ids an event touches (for diff attribution). */
const idsOf = (e: ProjectEvent): ReadonlyArray<string> => {
  const out: string[] = []
  const v = e as unknown as Record<string, unknown>
  for (const k of ["goalId", "kpiId", "areaId", "agentId", "itemId", "edgeId"])
    if (typeof v[k] === "string") out.push(v[k] as string)
  for (const k of ["goal", "kpi", "area", "agent", "item", "edge"]) {
    const r = v[k] as { id?: unknown } | undefined
    if (r && typeof r.id === "string") out.push(r.id)
  }
  const ref = v.ref as { id?: unknown } | undefined
  if (ref && typeof ref.id === "string") out.push(ref.id)
  return out
}

const PLANNING: ReadonlySet<ItemStatus> = new Set(["triage", "backlog", "todo", "in_progress"])

/**
 * Undo of one changeset: the inverse commands, newest first, built from the events' `before` values. Valid only if
 * no later change touched the same records (detected by comparing current values with what the changeset wrote).
 * A changeset made before the work-model update (its events upcast to `LegacyInitiative*`) is refused: revert instead.
 */
export const inverseOf = (
  state: ProjectState,
  changesetEvents: ReadonlyArray<ProjectEvent>,
):
  | { readonly changes: ReadonlyArray<GraphCommand> }
  | { readonly conflict: ReadonlyArray<EntityRef> }
  | { readonly legacy: ReadonlyArray<EntityRef> } => {
  const legacy = changesetEvents.flatMap((e) =>
    e._tag === "LegacyInitiativeUpdated" || e._tag === "LegacyInitiativeStatusChanged"
      ? [{ _tag: "item" as const, id: e.itemId }]
      : [],
  )
  if (legacy.length > 0) return { legacy }
  const changes: GraphCommand[] = []
  const conflict: EntityRef[] = []
  const curGoal = (id: string) => state.goals.find((x) => x.id === id)
  const curItem = (id: string) => state.items.find((x) => x.id === id)
  const curArea = (id: string) => state.areas.find((x) => x.id === id)
  const curAgent = (id: string) => state.agents.find((x) => x.id === id)
  const curKpi = (id: string) => state.kpis.find((x) => x.id === id)
  const rankOf = (ref: Extract<ProjectEvent, { _tag: "RankChanged" }>["ref"]) =>
    ref._tag === "goal" ? curGoal(ref.id)?.rank : curItem(ref.id)?.rank
  const targetOf = (subject: Extract<ProjectEvent, { _tag: "TargetChanged" }>["subject"]) =>
    subject._tag === "goal" ? curGoal(subject.id)?.target : curItem(subject.id)?.bet?.success.condition
  const matches = (rec: object | undefined, patch: object) =>
    rec !== undefined &&
    Object.entries(patch).every(([k, v]) => v === undefined || same((rec as Record<string, unknown>)[k], v))
  for (const e of [...changesetEvents].reverse()) {
    switch (e._tag) {
      case "GoalCreated":
        if (curGoal(e.goal.id)?.status === "active")
          changes.push({ _tag: "SetGoalStatus", goalId: e.goal.id, status: "abandoned", note: "undo" })
        break
      case "GoalUpdated":
        if (!matches(curGoal(e.goalId), e.patch)) conflict.push({ _tag: "goal", id: e.goalId })
        else changes.push({ _tag: "UpdateGoal", goalId: e.goalId, patch: e.before })
        break
      case "GoalStatusChanged":
        if (curGoal(e.goalId)?.status !== e.status) conflict.push({ _tag: "goal", id: e.goalId })
        else changes.push({ _tag: "SetGoalStatus", goalId: e.goalId, status: e.before, note: "undo" })
        break
      case "TargetChanged":
        if (!same(targetOf(e.subject), e.condition)) conflict.push(e.subject)
        else if (e.before) changes.push({ _tag: "ChangeTarget", subject: e.subject, condition: e.before })
        break
      case "KpiUpdated":
        if (!matches(curKpi(e.kpiId), e.patch)) conflict.push({ _tag: "kpi", id: e.kpiId })
        else changes.push({ _tag: "UpdateKpi", kpiId: e.kpiId, patch: e.before })
        break
      case "AreaCreated":
        if (curArea(e.area.id)?.status === "active") changes.push({ _tag: "ArchiveArea", areaId: e.area.id })
        break
      case "AreaUpdated":
        if (!matches(curArea(e.areaId), e.patch)) conflict.push({ _tag: "area", id: e.areaId })
        else changes.push({ _tag: "UpdateArea", areaId: e.areaId, patch: e.before })
        break
      case "AreaCharterRevised":
        if (!same(curArea(e.areaId)?.charter, e.charter)) conflict.push({ _tag: "area", id: e.areaId })
        else changes.push({ _tag: "ReviseAreaCharter", areaId: e.areaId, charter: e.before })
        break
      case "AgentHired":
        if (curAgent(e.agent.id)?.status !== "retired")
          changes.push({ _tag: "SetAgentStatus", agentId: e.agent.id, status: "retired" })
        break
      case "AgentUpdated":
        if (!matches(curAgent(e.agentId), e.patch)) conflict.push({ _tag: "agent", id: e.agentId })
        else changes.push({ _tag: "UpdateAgent", agentId: e.agentId, patch: e.before })
        break
      case "AgentCharterRevised":
        if (!same(curAgent(e.agentId)?.charter, e.charter)) conflict.push({ _tag: "agent", id: e.agentId })
        else changes.push({ _tag: "ReviseAgentCharter", agentId: e.agentId, charter: e.before })
        break
      case "AgentMindChanged":
        if (!same(curAgent(e.agentId)?.mind, e.mind)) conflict.push({ _tag: "agent", id: e.agentId })
        else changes.push({ _tag: "ChangeAgentMind", agentId: e.agentId, mind: e.before })
        break
      case "BriefRevised":
        // decision 0049: no conflict entry; a brief changed since fails applyGraph's baseHash check (stale-brief Invalid)
        changes.push({ _tag: "ReviseBrief", body: e.before, baseHash: briefHash(e.body), why: "undo" })
        break
      case "AgentStatusChanged":
        if (e.before !== "retired" && curAgent(e.agentId)?.status === e.status && e.status !== "retired")
          changes.push({ _tag: "SetAgentStatus", agentId: e.agentId, status: e.before })
        else conflict.push({ _tag: "agent", id: e.agentId })
        break
      case "ItemCreated": {
        const t = curItem(e.item.id)
        if (t && isOpenItem(t))
          changes.push({
            _tag: "CloseItem",
            itemId: t.id,
            as: "cancelled",
            resolution: { _tag: "wont_do" },
            note: "undo",
          })
        break
      }
      case "ItemUpdated": {
        // wave E (decision 0063): who asked and a lend are Minga's to write; the undo is a new edit (it shapes again)
        const { askedBy: _a, lentRunnerId: _l, ...patch } = e.patch
        const { askedBy: _ba, lentRunnerId: _bl, ...before } = e.before
        if (Object.keys(patch).length === 0) break
        if (!matches(curItem(e.itemId), patch)) conflict.push({ _tag: "item", id: e.itemId })
        else changes.push({ _tag: "UpdateItem", itemId: e.itemId, patch: before })
        break
      }
      case "ItemMoved": {
        const t = curItem(e.itemId)
        if (t?.parentId !== e.parentId) conflict.push({ _tag: "item", id: e.itemId })
        else {
          changes.push({ _tag: "MoveItem", itemId: e.itemId, parentId: e.before })
          if (e.before === null && e.beforeGoalIds.length > 0)
            changes.push({ _tag: "UpdateItem", itemId: e.itemId, patch: { goalIds: e.beforeGoalIds } })
        }
        break
      }
      case "ItemAssigned": {
        const t = curItem(e.itemId)
        const ownerSame = same(t?.owner ?? null, e.owner)
        const assigneeSame = e.assignee === undefined || same(t?.assignee ?? null, e.assignee)
        if (!t || !ownerSame || !assigneeSame) conflict.push({ _tag: "item", id: e.itemId })
        else
          changes.push({
            _tag: "AssignItem",
            itemId: e.itemId,
            owner: e.before,
            ...(e.assignee !== undefined && e.beforeAssignee !== undefined
              ? { assignee: e.beforeAssignee }
              : {}),
          })
        break
      }
      case "ChecksRevised": {
        const t = curItem(e.itemId)
        const { round: _r, stage: _s, ...wrote } = e.review
        const { round: _cr, stage: _cs, ...now } = t?.review ?? e.review
        if (!t || !same(t.checks, e.checks) || !same(now, wrote)) {
          conflict.push({ _tag: "item", id: e.itemId })
          break
        }
        const { round: _br, stage: _bs, ...review } = e.before.review
        changes.push({ _tag: "ReviseChecks", itemId: e.itemId, checks: e.before.checks, review })
        break
      }
      case "ItemStatusChanged":
        // planning moves go back; executions, closes and cancellations don't reopen (undo of those needs a new plan)
        if (
          e.cause._tag === "planned" &&
          PLANNING.has(e.from) &&
          e.from !== "in_review" &&
          curItem(e.itemId)?.status === e.to
        )
          changes.push({
            _tag: "MoveItem",
            itemId: e.itemId,
            status: e.from as "triage" | "backlog" | "todo" | "in_progress",
          })
        else conflict.push({ _tag: "item", id: e.itemId })
        break
      case "RankChanged":
        if (rankOf(e.ref) !== e.rank) conflict.push(e.ref)
        else changes.push({ _tag: "Reprioritize", ref: e.ref, rank: e.before })
        break
      case "EdgeAdded":
        if (state.edges.some((x) => x.id === e.edge.id))
          changes.push({ _tag: "RemoveEdge", edgeId: e.edge.id })
        break
      case "EdgeRemoved":
        if (state.edges.some((x) => x.id === e.edge.id)) {
          conflict.push({ _tag: "edge", id: e.edge.id })
          break
        }
        changes.push({
          _tag: "AddEdge",
          edgeId: e.edge.id,
          kind: e.edge.kind,
          from: e.edge.from,
          to: e.edge.to,
          ...(e.edge.interfaceKey ? { interfaceKey: e.edge.interfaceKey } : {}),
          ...(e.edge.note ? { note: e.edge.note } : {}),
        })
        break
      case "AreaArchived":
      case "RequestDecided":
        // archives and request decisions don't reopen: undo of those needs a new plan
        conflict.push(
          e._tag === "AreaArchived" ? { _tag: "area", id: e.areaId } : { _tag: "item", id: e.itemId },
        )
        break
      default:
        break // execution facts, policies, markers: nothing to invert
    }
  }
  return conflict.length > 0 ? { conflict } : { changes }
}

/**
 * Wave A §5: can this changeset be undone right after it applied? `events` are the changeset's applied events (the
 * commit's). True when its inverse has neither a legacy nor a conflict part and does something: a close, a cancel or
 * any non-planning status change conflicts always, so those never offer Undo.
 */
export const undoable = (after: ProjectState, events: ReadonlyArray<ProjectEvent>): boolean => {
  const inv = inverseOf(after, events)
  return "changes" in inv && inv.changes.length > 0
}

/** A planning status a MoveItem can set. */
const planningStatus = (s: ItemStatus): s is "triage" | "backlog" | "todo" | "in_progress" => PLANNING.has(s)

/**
 * Revert to version v, forward-only: diff the current plan against `target` and turn the difference into commands.
 * New work after v is cancelled (never deleted), removed edges come back, changed plan fields are restored.
 */
export const revertTo = (current: ProjectState, target: ProjectState): ReadonlyArray<GraphCommand> => {
  const out: GraphCommand[] = []
  // goals
  for (const g of current.goals) {
    const t = target.goals.find((x) => x.id === g.id)
    if (!t) {
      if (g.status === "active")
        out.push({ _tag: "SetGoalStatus", goalId: g.id, status: "abandoned", note: "revert" })
      continue
    }
    const patch = diffFields(g, t, ["title", "why", "body", "deadline", "kpiIds", "tags", "criteria"])
    if (Object.keys(patch).length > 0) out.push({ _tag: "UpdateGoal", goalId: g.id, patch: patch as never })
    if (t.target !== null && !same(g.target, t.target))
      out.push({ _tag: "ChangeTarget", subject: { _tag: "goal", id: g.id }, condition: t.target })
    if (g.status !== t.status)
      out.push({ _tag: "SetGoalStatus", goalId: g.id, status: t.status, note: "revert" })
    if (g.rank !== t.rank) out.push({ _tag: "Reprioritize", ref: { _tag: "goal", id: g.id }, rank: t.rank })
  }
  // areas and agents
  for (const a of current.areas) {
    const t = target.areas.find((x) => x.id === a.id)
    if (!t) {
      if (a.status === "active") out.push({ _tag: "ArchiveArea", areaId: a.id })
      continue
    }
    const patch = diffFields(a, t, [
      "name",
      "color",
      "intakeAgentId",
      "placement",
      "powers",
      "wip",
      "interfaces",
    ])
    if (Object.keys(patch).length > 0) out.push({ _tag: "UpdateArea", areaId: a.id, patch: patch as never })
    if (!same(a.charter, t.charter)) out.push({ _tag: "ReviseAreaCharter", areaId: a.id, charter: t.charter })
  }
  for (const a of current.agents) {
    const t = target.agents.find((x) => x.id === a.id)
    if (!t) continue // hires are reverted by people (retiring needs the items gone first)
    const patch = diffFields(a, t, ["name", "title", "avatar", "hands", "wake"])
    if (Object.keys(patch).length > 0) out.push({ _tag: "UpdateAgent", agentId: a.id, patch: patch as never })
    if (!same(a.charter, t.charter))
      out.push({ _tag: "ReviseAgentCharter", agentId: a.id, charter: t.charter })
    if (!same(a.mind, t.mind)) out.push({ _tag: "ChangeAgentMind", agentId: a.id, mind: t.mind })
  }
  // items: new open items are cancelled (top-most only; cancel cascades)
  const cancelled = new Set<string>()
  const topMostNew = (it: Item): boolean => {
    for (let p = it.parentId; p !== null; ) {
      if (cancelled.has(p)) return false
      const parent = current.items.find((x) => x.id === p)
      if (!parent) break
      if (!target.items.some((x) => x.id === parent.id) && isOpenItem(parent)) return false
      p = parent.parentId
    }
    return true
  }
  for (const it of current.items) {
    const t = target.items.find((x) => x.id === it.id)
    if (!t) {
      if (!isOpenItem(it)) continue
      if (topMostNew(it))
        out.push({
          _tag: "CloseItem",
          itemId: it.id,
          as: "cancelled",
          resolution: { _tag: "wont_do" },
          note: "revert",
        })
      cancelled.add(it.id)
      continue
    }
    if (!isOpenItem(it)) continue
    // a bet's verdict is an execution fact (never reverted); its success condition goes back through ChangeTarget
    let targetBet = t.bet
    let changeTarget: GraphCommand | null = null
    if (it.bet && targetBet) {
      if (!same(it.bet.success.condition, targetBet.success.condition))
        changeTarget = {
          _tag: "ChangeTarget",
          subject: { _tag: "item", id: it.id },
          condition: targetBet.success.condition,
        }
      targetBet = {
        ...targetBet,
        verdict: it.bet.verdict,
        success: { ...targetBet.success, condition: it.bet.success.condition },
      }
    }
    const patch = diffFields(it, { ...t, bet: targetBet }, [
      "title",
      "why",
      "body",
      "type",
      "kind",
      "tags",
      "paths",
      "integrations",
      "placement",
      "hands",
      "dueAt",
      "notBefore",
      "bet",
      ...(it.parentId === null && t.parentId === null ? (["goalIds"] as const) : []),
    ])
    if (Object.keys(patch).length > 0) out.push({ _tag: "UpdateItem", itemId: it.id, patch: patch as never })
    if (changeTarget) out.push(changeTarget)
    if (it.parentId !== t.parentId) out.push({ _tag: "MoveItem", itemId: it.id, parentId: t.parentId })
    if (!same(it.owner, t.owner) || !same(it.assignee, t.assignee))
      out.push({
        _tag: "AssignItem",
        itemId: it.id,
        ...(!same(it.owner, t.owner) ? { owner: t.owner } : {}),
        ...(!same(it.assignee, t.assignee) ? { assignee: t.assignee } : {}),
      })
    if (!same(it.checks, t.checks)) {
      const { round: _r, stage: _s, ...review } = t.review
      out.push({ _tag: "ReviseChecks", itemId: it.id, checks: t.checks, review })
    }
    // planning moves of containers and plannable items go back (a leaf's todo/in_progress follows its runs)
    if (
      it.status !== t.status &&
      planningStatus(it.status) &&
      planningStatus(t.status) &&
      (isContainer(current, it) || isPlannable(it) || it.status === "triage" || it.status === "backlog")
    )
      out.push({ _tag: "MoveItem", itemId: it.id, status: t.status })
    if (it.rank !== t.rank) out.push({ _tag: "Reprioritize", ref: { _tag: "item", id: it.id }, rank: t.rank })
  }
  // edges: new ones removed, removed ones added back
  for (const e of current.edges)
    if (!target.edges.some((x) => x.id === e.id)) out.push({ _tag: "RemoveEdge", edgeId: e.id })
  for (const e of target.edges)
    if (!current.edges.some((x) => x.id === e.id))
      out.push({
        _tag: "AddEdge",
        edgeId: e.id,
        kind: e.kind,
        from: e.from,
        to: e.to,
        ...(e.interfaceKey ? { interfaceKey: e.interfaceKey } : {}),
        ...(e.note ? { note: e.note } : {}),
      })
  return out
}
